<template>
  <div v-for="n in 1" class="grid justify-content-center m-0">
    <div class="col-12 md:col-10 lg:col-8">
      <div class="flex flex-column p-3 gap-3">
        <h1 class="title m-0">Informes disponibles</h1>

        <InputText
          type="text"
          v-model="search"
          placeholder="Buscar por nombre"
        />

        <template v-if="showMustBeLogged">
          Debe iniciar sesión para ver los informes
        </template>
        <template v-else>
          <div v-if="loadingReports" class="category-title text-center">
            <div class="mb-2">Cargando informes...</div>
            <ProgressBar mode="indeterminate" style="height: 6px"></ProgressBar>
          </div>

          <template v-else>
            <!-- Reports list -->
            <div v-if="filteredReports.length > 0" class="grid mt-3">
              <div
                v-for="(filteredReport, index) in filteredReports"
                :key="index"
                class="col-12"
              >
                <div class="title category-title">
                  {{ filteredReport.categoria }}
                </div>
                <div class="grid py-3">
                  <div
                    v-for="(report, index2) in filteredReport.informes"
                    :key="index2"
                    class="col-12 xl:col-6"
                  >
                    <Report
                      :id="report.id"
                      :alias="report.alias"
                      :titulo="report.titulo"
                      :status="
                        report.esPublico == null || report.esPublico == false
                          ? 'private'
                          : 'public'
                      "
                      :descripcion="report.descripcion"
                      :usuarios="report.usuarios"
                      :tipoInforme="report.tipoInforme"
                      :gestionaUsuarios="report.gestionaUsuarios"
                      :actualizaArchivos="report.actualizaArchivos"
                      :url="report.url"
                      :tieneLandingPage="report.tieneLandingPage"
                      :habilitado="report.habilitado"
                      :esFavorito="report.esFavorito"
                      :publicoPrivado="report.publicoPrivado"
                      :visible="report.visible"
                      @onUpdate="handleUpdate"
                      @onShowUsers="handleShowUsers"
                      @onToggleFavorite="handleToggleFavorite"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div v-else>No hay informes disponibles</div>
          </template>
        </template>
      </div>
    </div>
  </div>

  <!-- Modal -->
  <ReportsDialogView ref="modalRef" @saved="getReports(search)" />

  <!-- Report has users modal -->
  <ReportHasUsersDialogView ref="reportHasUsersDialogView" />
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import InputText from "primevue/inputtext";
import ProgressBar from "primevue/progressbar";
import { watchDebounced } from "@vueuse/core";

import { isLogged } from "@/composables/_commons/auth.js";
import {
  userReportListAll,
  userReportListByFilter,
} from "@/api/reports/reports.api.js";
import ReportHasUsersDialogView from "@/pages/_root/report-has-users-dialog/ReportHasUsersDialogView.vue"; // eslint-disable-line
import Report from "@/components/partials/Report.vue";
import ReportsDialogView from "@/pages/_root/reports-dialog/ReportsDialogView.vue";

// #region Props, Emits
// #endregion

// #region  Params, Ref, Reactives, Computed
const search = ref("");
const reports = ref([]);
const filteredReports = ref([]);
const reportHasUsersDialogView = ref(null);
const modalRef = ref(null);
const showMustBeLogged = ref(false);
const loadingReports = ref(false);
// #endregion

// #region Functions
async function getReports(filter = null) {
  loadingReports.value = true;
  let response = null;

  if (!filter) {
    response = await userReportListAll();
  } else {
    response = await userReportListByFilter(filter);
  }

  if (response.response?.status == 200) {
    reports.value = response.data;
    reports.value = groupReportsByCategory(reports.value);

    // Convert the object to an array
    reports.value = Object.entries(reports.value).map(([key, value]) => {
      return { categoria: key, informes: value };
    });

    // Move the favorites category to the first position
    const favoritesIndex = reports.value.findIndex(
      (report) => report.categoria == "Favoritos"
    );

    if (favoritesIndex > 0) {
      const favorites = reports.value.splice(favoritesIndex, 1);
      reports.value.unshift(favorites[0]);
    }

    filteredReports.value = [];

    nextTick(() => {
      if (reports.value.length > 0) {
        filteredReports.value = JSON.parse(JSON.stringify(reports.value));
      } else {
        filteredReports.value = [];
      }
    });
  }

  loadingReports.value = false;
}

function groupReportsByCategory(reports) {
  if (reports != null && reports != undefined) {
    const favorites = [];

    // Group the reports by category field inside the report object
    const groupedReports = reports.reduce((acc, report) => {
      if (!acc[report.categoria]) {
        acc[report.categoria] = [];
      }

      if (report.esFavorito) {
        favorites.push(report);
      }

      acc[report.categoria].push(report);

      return acc;
    }, {});

    // Add the favorites category to the grouped reports
    if (favorites.length > 0) {
      groupedReports["Favoritos"] = favorites;
    }

    return groupedReports;
  } else {
    return [];
  }
}

// Open the modal with the data
function handleUpdate(data) {
  modalRef.value.getAditionalData(data, data.tipoInforme);
}

// Open de report has users modal
function handleShowUsers(data) {
  reportHasUsersDialogView.value.showModal(data);
}

function handleToggleFavorite() {
  getReports(search.value);
}
// #endregion

// #region Effects, Hooks
onMounted(async () => {
  if (await isLogged()) {
    getReports();
  } else {
    showMustBeLogged.value = true;
  }
});

watchDebounced(
  search,
  (newValue) => {
    getReports(newValue);
  },
  { debounce: 150 }
);
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.title {
  font-size: 1.5rem;
  font-weight: 600;
  border-left: 4px solid #f9d017;
  padding-left: 10px;

  &.category-title {
    font-size: 1rem;
  }
}

.progress-spinner {
  height: 5rem;

  :deep(.p-progress-spinner-svg) {
    .p-progress-spinner-circle {
      stroke: $primary !important;
    }
  }
}
</style>
